import axios from "axios";
import { UserInfo } from "../UserInfoForm";

// Define interfaces for API responses
interface RankingData {
  first_name: string;
  last_name: string;
  top_category: string;
  top_score: number;
  completed_at: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Export the QuizResult interface
export interface QuizResult {
  userInfo: UserInfo;
  scores: { [key: string]: number };
  answers: Array<{
    questionIndex: number;
    selectedOptions: string[];
  }>;
  completedAt: Date;
}

// Get the base URL from window.location in production
const API_BASE =
  process.env.NODE_ENV === "production"
    ? `${window.location.origin}/api`
    : "http://localhost:3000/api";

export class DatabaseService {
  static async saveQuizResult(result: QuizResult): Promise<void> {
    // In development mode without a backend, just log the result
    if (process.env.NODE_ENV === "development") {
      console.log("[DEV MODE] Quiz result (would be saved in production):", result);
      console.log("[DEV MODE] Skipping API call - backend not available in local development");
      // Simulate async operation
      await new Promise(resolve => setTimeout(resolve, 500));
      return;
    }

    // Validate the result object
    if (!result.userInfo) {
      throw new DatabaseError(
        DatabaseErrorType.VALIDATION_ERROR,
        "User information is missing",
      );
    }

    if (!result.scores || Object.keys(result.scores).length === 0) {
      throw new DatabaseError(
        DatabaseErrorType.VALIDATION_ERROR,
        "No scores provided",
      );
    }

    if (!Array.isArray(result.answers) || result.answers.length === 0) {
      throw new DatabaseError(
        DatabaseErrorType.VALIDATION_ERROR,
        "No answers provided",
      );
    }

    try {
      console.log("Sending quiz result to server:", result); // Debug log

      const payload = {
        ...result,
        completedAt: result.completedAt.toISOString(),
      };

      const response = await axios.post<ApiResponse<void>>(
        `${API_BASE}/quiz-results`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        },
      );

      this.validateResponse<ApiResponse<void>>(response.data);

      if (!response.data.success) {
        throw new DatabaseError(
          DatabaseErrorType.SERVER_ERROR,
          response.data.error || "Failed to save quiz result",
        );
      }

      console.log("Quiz result saved successfully:", response.data);
    } catch (error) {
      console.error("Error in saveQuizResult:", error);

      if (axios.isAxiosError(error)) {
        if (error.response) {
          throw new DatabaseError(
            DatabaseErrorType.SERVER_ERROR,
            `Server error: ${error.response.data.error || error.response.statusText}`,
            error,
          );
        } else if (error.request) {
          throw new DatabaseError(
            DatabaseErrorType.NETWORK_ERROR,
            "No response received from server",
            error,
          );
        }
      }

      if (error instanceof DatabaseError) {
        throw error;
      }

      throw new DatabaseError(
        DatabaseErrorType.UNKNOWN_ERROR,
        "An unexpected error occurred",
        error instanceof Error ? error : undefined,
      );
    }
  }

  static async getRankings(): Promise<RankingData[]> {
    // In development mode without a backend, return mock data
    if (process.env.NODE_ENV === "development") {
      console.log("[DEV MODE] Returning mock rankings - backend not available in local development");
      // Simulate async operation
      await new Promise(resolve => setTimeout(resolve, 500));
      return [
        {
          first_name: "John",
          last_name: "Doe",
          top_category: "IT/COMPUTERS/DATA SCIENCE/ROBOTICS",
          top_score: 85,
          completed_at: new Date().toISOString()
        },
        {
          first_name: "Jane",
          last_name: "Smith",
          top_category: "BUSINESS/MANAGEMENT/ENTREPRENEURSHIP/HOSPITALITY",
          top_score: 78,
          completed_at: new Date().toISOString()
        }
      ];
    }

    try {
      const response = await axios.get<ApiResponse<RankingData[]>>(
        `${API_BASE}/rankings`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 5000,
        },
      );

      this.validateResponse<ApiResponse<RankingData[]>>(response.data);

      if (!response.data.success || !response.data.data) {
        throw new DatabaseError(
          DatabaseErrorType.SERVER_ERROR,
          "No rankings data received",
        );
      }

      return response.data.data;
    } catch (error) {
      console.error("Error fetching rankings:", error);

      if (axios.isAxiosError(error)) {
        if (error.response) {
          throw new DatabaseError(
            DatabaseErrorType.SERVER_ERROR,
            `Server error: ${error.response.data.error || error.response.statusText}`,
            error,
          );
        } else if (error.request) {
          throw new DatabaseError(
            DatabaseErrorType.NETWORK_ERROR,
            "No response received from server",
            error,
          );
        }
      }

      throw new DatabaseError(
        DatabaseErrorType.UNKNOWN_ERROR,
        "Failed to fetch rankings",
        error instanceof Error ? error : undefined,
      );
    }
  }

  // Helper method to check if response is error
  private static isErrorResponse(
    response: unknown,
  ): response is { error: string } {
    return (
      typeof response === "object" && response !== null && "error" in response
    );
  }

  // Helper method to validate responses
  private static validateResponse<T>(response: unknown): asserts response is T {
    if (!response || typeof response !== "object") {
      throw new DatabaseError(
        DatabaseErrorType.VALIDATION_ERROR,
        "Invalid response from server",
      );
    }
  }

  // Retry mechanism for failed requests
  static async retryOperation<T>(
    operation: () => Promise<T>,
    maxRetries = 3,
    delay = 1000,
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let i = 0; i < maxRetries; i++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (i < maxRetries - 1) {
          await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)));
          continue;
        }
        break;
      }
    }

    throw lastError || new Error("Operation failed after retries");
  }
}

// Export error types for better error handling
export enum DatabaseErrorType {
  VALIDATION_ERROR = "VALIDATION_ERROR",
  NETWORK_ERROR = "NETWORK_ERROR",
  SERVER_ERROR = "SERVER_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export class DatabaseError extends Error {
  constructor(
    public type: DatabaseErrorType,
    message: string,
    public originalError?: Error,
  ) {
    super(message);
    this.name = "DatabaseError";
  }
}

// Export constants
export const API_ENDPOINTS = {
  QUIZ_RESULTS: `${API_BASE}/quiz-results`,
  RANKINGS: `${API_BASE}/rankings`,
};
